/**
 * FolderCard - Componente para exibir pasta em grid e lista
 * 
 * Inspirado no Google Drive. Suporta:
 * - Grid view (card com ícone grande)
 * - List view (linha compacta)
 * - Drag source (arrastar pasta)
 * - Drop target (soltar arquivos/pastas dentro)
 * - Menu de contexto (renomear, mover, excluir, cor)
 * - Seleção via checkbox
 * - Tooltip de ajuda
 */

import React, { useRef, useState } from 'react'
import {
  Box,
  Card,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  alpha,
  keyframes
} from '@mui/material'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PaletteIcon from '@mui/icons-material/Palette'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import type { TranscriptionFolder } from '../../../types/folder'

const popIn = keyframes`
  0% { transform: scale(0.95); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`

const dropPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(0.98); }
  100% { transform: scale(1); }
`

interface Props {
  folder: TranscriptionFolder
  view: 'grid' | 'list'
  selected?: boolean
  onSelect?: (folderId: string, checked: boolean) => void
  onClick: (folderId: string) => void
  onRename?: (folder: TranscriptionFolder) => void
  onMove?: (folder: TranscriptionFolder) => void
  onDelete?: (folder: TranscriptionFolder) => void
  onChangeColor?: (folder: TranscriptionFolder) => void
  /** Quando itens são soltos nesta pasta (array de IDs) */
  onDropItems?: (targetFolderId: string, itemIds: string[], itemType: 'file' | 'folder') => void
  animationDelay?: number
}

export default function FolderCard({
  folder,
  view,
  selected = false,
  onSelect,
  onClick,
  onRename,
  onMove,
  onDelete,
  onChangeColor,
  onDropItems,
  animationDelay = 0
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const folderColor = folder.color || '#90CAF9'

  // ── Drag Source (arrastar esta pasta) ──
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'folder',
      id: folder.id,
      name: folder.name
    }))
    e.dataTransfer.effectAllowed = 'move'
  }

  // ── Drop Target (soltar itens dentro desta pasta) ──
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Verifica se tem dados válidos
    const types = e.dataTransfer.types
    if (types.includes('application/json') || types.includes('text/plain')) {
      e.dataTransfer.dropEffect = 'move'
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'))
      if (data && data.id && data.id !== folder.id && onDropItems) {
        const ids = data.ids || [data.id]
        onDropItems(folder.id, ids, data.type || 'file')
      }
    } catch { /* ignore */ }
  }

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }

  const handleMenuClose = () => setMenuAnchor(null)

  const handleMenuAction = (action: () => void) => {
    handleMenuClose()
    action()
  }

  // ═══════════════════════════════════════════
  // GRID VIEW
  // ═══════════════════════════════════════════
  if (view === 'grid') {
    return (
      <Box sx={{ position: 'relative' }}>
        <Card
          ref={cardRef}
          draggable
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => onClick(folder.id)}
          elevation={isDragOver ? 6 : isHovered ? 2 : 0}
          sx={{
            cursor: 'pointer',
            position: 'relative',
            borderRadius: 3,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: isDragOver
              ? 'none'
              : `${popIn} 0.3s ease-out`,
            animationDelay: `${animationDelay}ms`,
            animationFillMode: 'backwards',
            transform: isDragOver ? 'scale(1.03)' : 'scale(1)',
            border: isDragOver
              ? `2px dashed ${folderColor}`
              : `1px solid ${alpha(folderColor, selected ? 0.5 : 0.15)}`,
            bgcolor: isDragOver
              ? alpha(folderColor, 0.12)
              : selected
                ? alpha(folderColor, 0.06)
                : 'background.paper',
            boxShadow: isDragOver
              ? `0 8px 24px ${alpha(folderColor, 0.35)}, 0 0 0 1px ${alpha(folderColor, 0.2)}`
              : 'none',
            '&:hover': {
              transform: isDragOver ? 'scale(1.03)' : 'translateY(-2px)',
              borderColor: alpha(folderColor, 0.4)
            },
            overflow: 'visible'
          }}
        >
          {/* Checkbox de seleção */}
          {onSelect && (isHovered || selected) && (
            <Checkbox
              size="small"
              checked={selected}
              onChange={(e) => {
                e.stopPropagation()
                onSelect(folder.id, e.target.checked)
              }}
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                zIndex: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                p: 0.25
              }}
            />
          )}

          {/* Menu dots */}
          {(isHovered || Boolean(menuAnchor)) && (
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                zIndex: 2,
                bgcolor: alpha('#fff', 0.9),
                '&:hover': { bgcolor: '#fff' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}

          {/* Conteúdo do card — layout horizontal compacto */}
          <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64 }}>
            {/* Ícone compacto */}
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                bgcolor: alpha(folderColor, isDragOver ? 0.18 : 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'background-color 0.2s ease'
              }}
            >
              {isDragOver ? (
                <FolderOpenIcon sx={{ fontSize: 22, color: folderColor }} />
              ) : (
                <FolderIcon sx={{ fontSize: 22, color: folderColor }} />
              )}
            </Box>

            {/* Nome e contagem */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={folder.name} enterDelay={600}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ lineHeight: 1.3 }}
                >
                  {folder.name}
                </Typography>
              </Tooltip>
              {folder.itemCount != null && folder.itemCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.7rem', lineHeight: 1.2 }}
                >
                  {folder.itemCount} {folder.itemCount === 1 ? 'arquivo' : 'arquivos'}
                </Typography>
              )}
            </Box>
          </Box>
        </Card>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
        >
          {/* Opção de seleção */}
          {onSelect && (
            <MenuItem
              onClick={() => handleMenuAction(() => onSelect(folder.id, !selected))}
              sx={{ color: selected ? 'primary.main' : 'inherit' }}
            >
              <ListItemIcon>
                {selected ? (
                  <CheckBoxIcon fontSize="small" color="primary" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{selected ? 'Desmarcar' : 'Selecionar'}</ListItemText>
            </MenuItem>
          )}
          {onRename && (
            <MenuItem onClick={() => handleMenuAction(() => onRename(folder))}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Renomear</ListItemText>
            </MenuItem>
          )}
          {onChangeColor && (
            <MenuItem onClick={() => handleMenuAction(() => onChangeColor(folder))}>
              <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Alterar cor</ListItemText>
            </MenuItem>
          )}
          {onMove && (
            <MenuItem onClick={() => handleMenuAction(() => onMove(folder))}>
              <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Mover</ListItemText>
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem onClick={() => handleMenuAction(() => onDelete(folder))} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Excluir pasta</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>
    )
  }

  // ═══════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════
  return (
    <Box
      ref={cardRef}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(folder.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1, sm: 1.5 },
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: isDragOver
          ? 'none'
          : `${popIn} 0.25s ease-out`,
        animationDelay: `${animationDelay}ms`,
        animationFillMode: 'backwards',
        transform: isDragOver ? 'scaleY(1.05)' : 'scaleY(1)',
        borderBottom: '1px solid #f0f0f0',
        borderLeft: isDragOver
          ? `4px solid ${folderColor}`
          : `3px solid ${folderColor}`,
        bgcolor: isDragOver
          ? alpha(folderColor, 0.12)
          : selected
            ? alpha(folderColor, 0.04)
            : alpha(folderColor, 0.02),
        boxShadow: isDragOver
          ? `0 4px 12px ${alpha(folderColor, 0.25)}, inset 0 0 0 1px ${alpha(folderColor, 0.3)}`
          : 'none',
        '&:hover': {
          bgcolor: alpha(folderColor, 0.06),
          '& .folder-actions': { opacity: 1 },
          '& .selection-checkbox': { opacity: 1 }
        },
        '&:last-child': { borderBottom: 'none' }
      }}
    >
      {/* Checkbox de seleção - visível no hover em desktop */}
      {onSelect && (
        <Checkbox
          size="small"
          checked={selected}
          className="selection-checkbox"
          onChange={(e) => {
            e.stopPropagation()
            onSelect(folder.id, e.target.checked)
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            opacity: selected ? 1 : 0,
            transition: 'opacity 0.15s',
            '& .MuiSvgIcon-root': { fontSize: 20 }
          }}
        />
      )}

      {/* Ícone */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: 32, sm: 40 }, flexShrink: 0 }}>
        {isDragOver ? (
          <FolderOpenIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: folderColor }} />
        ) : (
          <FolderIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: folderColor }} />
        )}
      </Box>

      {/* Nome e info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{
            color: '#333',
            fontSize: { xs: '0.85rem', sm: '0.875rem' }
          }}
        >
          {folder.name}
        </Typography>
        <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem' }}>
          Pasta {folder.itemCount != null && folder.itemCount > 0 ? `• ${folder.itemCount} ${folder.itemCount === 1 ? 'item' : 'itens'}` : ''}
        </Typography>
      </Box>

      {/* Item count - visível em desktop */}
      <Typography
        variant="body2"
        sx={{
          color: '#666',
          width: 140,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' }
        }}
      >
        {folder.itemCount != null && folder.itemCount > 0 ? `${folder.itemCount} ${folder.itemCount === 1 ? 'item' : 'itens'}` : '—'}
      </Typography>

      {/* Spacer para alinhar com colunas de arquivo */}
      <Box sx={{ width: 80, flexShrink: 0, display: { xs: 'none', md: 'block' } }} />

      {/* Ações - botões em desktop */}
      <Box
        className="folder-actions"
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: { xs: 'none', sm: 'flex' },
          opacity: 0,
          transition: 'opacity 0.2s',
          gap: 0.5,
          flexShrink: 0
        }}
      >
        {onRename && (
          <Tooltip title="Renomear">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRename(folder) }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onChangeColor && (
          <Tooltip title="Alterar cor">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onChangeColor(folder) }}>
              <PaletteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(folder) }}
              color="error"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Menu em mobile */}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{ display: { xs: 'flex', sm: 'none' }, flexShrink: 0 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      {/* Context Menu (usado em mobile) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
      >
        {/* Opção de seleção para mobile */}
        {onSelect && (
          <MenuItem
            onClick={() => handleMenuAction(() => onSelect(folder.id, !selected))}
            sx={{ color: selected ? 'primary.main' : 'inherit' }}
          >
            <ListItemIcon>
              {selected ? (
                <CheckBoxIcon fontSize="small" color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{selected ? 'Desmarcar' : 'Selecionar'}</ListItemText>
          </MenuItem>
        )}
        {onRename && (
          <MenuItem onClick={() => handleMenuAction(() => onRename(folder))}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Renomear</ListItemText>
          </MenuItem>
        )}
        {onChangeColor && (
          <MenuItem onClick={() => handleMenuAction(() => onChangeColor(folder))}>
            <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Alterar cor</ListItemText>
          </MenuItem>
        )}
        {onMove && (
          <MenuItem onClick={() => handleMenuAction(() => onMove(folder))}>
            <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mover</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={() => handleMenuAction(() => onDelete(folder))} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Excluir pasta</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
